import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema, timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt'
import { toast } from 'sonner'

import {
  CreateUserSchedulingRequestSchema,
  DeleteUserSchedulingRequestSchema,
  UpdateUserSchedulingRequestSchema,
} from '@/gen/cortado/v1/cortado_pb'
import type { EventStaffCandidate, ListEventStaffAvailabilityResponse } from '@/gen/cortado/v1/event_staffing_pb'
import { UserSchedulingRoleEnum, UserSchedulingStatusEnum } from '@/gen/cortado/v1/settings_pb'
import {
  useCreateUserScheduling,
  useDeleteUserScheduling,
  useEventStaffAvailability,
  useUpdateUserScheduling,
  useUserSchedulings,
} from '@/stores/userScheduling.store'
import { organizationStore } from '@/stores/organization.store'
import { formatTimestamp } from '@/lib/date-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { formatFullName, cn } from '@/lib/utils'
import { getAvatarColor, getInitials } from '@/lib/avatar-utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { StaffShiftEditor, formatTimeRange, type Shift } from './staff-shift-editor'

interface AssignStaffSheetProps {
  eventId: string
  /** IANA zone of the event; the on-site window is shown in it. */
  eventTimezone?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When set (the job page), the footer links to the event's full logistics. */
  jobId?: string
}

const STATUS_LABEL: Partial<Record<UserSchedulingStatusEnum, string>> = {
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_PENDING]: 'Pending',
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_ACCEPTED]: 'Confirmed',
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_REJECTED]: 'Declined',
}

/** "Oct 22 · 7:00 AM – 1:30 PM PDT on site · needs 2" from the availability response. */
export function assignStaffSubtitle(availability: ListEventStaffAvailabilityResponse, eventTimezone?: string): string {
  const timeZone = availability.timezone || eventTimezone || undefined
  const parts: string[] = []
  if (availability.windowStart && availability.windowEnd) {
    const day = formatTimestamp(availability.windowStart, 'MMM d', timeZone)
    const range = formatTimeRange(availability.windowStart, availability.windowEnd, timeZone)
    parts.push(day, `${range} ${availability.windowFromTimeline ? 'on site' : 'service'}`)
  }
  parts.push(`needs ${availability.staffNeeded}`)
  return parts.join(' · ')
}

/**
 * The shift a new assignment gets: the whole on-site window, which is what
 * the person is busy for. Duration is in hours, to the minute.
 */
export function shiftFromWindow(availability: ListEventStaffAvailabilityResponse): Shift | undefined {
  if (!availability.windowStart || !availability.windowEnd) return undefined
  const start = timestampDate(availability.windowStart)
  const end = timestampDate(availability.windowEnd)
  const minutes = Math.round((end.getTime() - start.getTime()) / 60_000)
  if (minutes <= 0) return undefined
  return { scheduledAt: timestampFromDate(start), duration: minutes / 60 }
}

function candidateName(candidate: EventStaffCandidate): string {
  return formatFullName(candidate.firstName, candidate.lastName, candidate.email || 'Unknown')
}

function RoleTag({ role }: { role?: UserSchedulingRoleEnum }) {
  if (role === UserSchedulingRoleEnum.USER_SCHEDULING_ROLE_LEAD) {
    return (
      <Badge variant="info">Lead</Badge>
    )
  }
  return (
    <Badge variant="outline">Barista</Badge>
  )
}

/** The line under a person's name: their status here, or why they can't work it. */
function candidateDetail(candidate: EventStaffCandidate): { text: string; tone: 'muted' | 'warning' | 'success' } {
  const reason = candidate.reasons[0]
  const more = candidate.reasons.length > 1 ? ` (+${candidate.reasons.length - 1} more)` : ''
  if (candidate.schedulingId) {
    const status = STATUS_LABEL[candidate.status ?? UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_UNKNOWN] ?? 'Assigned'
    if (reason) return { text: `${status} · Also ${reason.label.charAt(0).toLowerCase()}${reason.label.slice(1)}${more}`, tone: 'warning' }
    return { text: status, tone: 'muted' }
  }
  if (reason) return { text: `Unavailable · ${reason.label}${more}`, tone: 'warning' }
  return { text: 'Available', tone: 'success' }
}

/**
 * Assign Staff sheet, shared by the job page's Logistics card and the event
 * page's Staff card. Lists every org member with their availability for the
 * event's on-site window (conflicting bookings and blackout dates show as
 * Unavailable), assigns with the shift prefilled to that window, shows and
 * edits each assigned person's shift, removes, and moves the Lead. The footer
 * shows "n of N needed".
 */
export function AssignStaffSheet({ eventId, eventTimezone, open, onOpenChange, jobId }: AssignStaffSheetProps) {
  const organizationId = useStore(organizationStore, (s) => s.selectedOrganizationId) ?? ''
  const { availability, isLoading, isError, error } = useEventStaffAvailability(eventId, open)
  // Candidate rows do not carry the shift, so it is joined from the event's schedulings.
  const { schedulings } = useUserSchedulings({ organizationId, eventId }, open)
  const createMutation = useCreateUserScheduling()
  const updateMutation = useUpdateUserScheduling()
  const deleteMutation = useDeleteUserScheduling()
  const [busyUserId, setBusyUserId] = useState<string | undefined>()

  const timeZone = availability?.timezone || eventTimezone || undefined
  const shift = availability ? shiftFromWindow(availability) : undefined
  const shiftBySchedulingId = new Map(schedulings.map((s) => [s.id, s]))
  const assigned = availability?.staffAssignedCount ?? 0
  const needed = availability?.staffNeeded ?? 0
  const ready = !!availability && assigned >= needed

  /** Runs one mutation with the row marked busy and a toast either way. True when it succeeded. */
  async function run(userId: string, action: () => Promise<unknown>, success: string, failure: string) {
    setBusyUserId(userId)
    try {
      await action()
      toast.success(success)
      return true
    } catch (err) {
      reportError(err, { feature: 'assign_staff_sheet', kind: 'mutation' })
      toast.error(failure, { description: getErrorDescription(err) })
      return false
    } finally {
      setBusyUserId(undefined)
    }
  }

  const assign = (candidate: EventStaffCandidate) => {
    if (!shift) return
    void run(
      candidate.userId,
      () =>
        createMutation.mutateAsync(
          create(CreateUserSchedulingRequestSchema, {
            eventId,
            userId: candidate.userId,
            scheduledAt: shift.scheduledAt,
            duration: shift.duration,
          }),
        ),
      `${candidateName(candidate)} assigned`,
      'Failed to assign staff',
    )
  }

  const remove = (candidate: EventStaffCandidate) => {
    if (!candidate.schedulingId) return
    const id = candidate.schedulingId
    void run(
      candidate.userId,
      () => deleteMutation.mutateAsync(create(DeleteUserSchedulingRequestSchema, { id })),
      `${candidateName(candidate)} removed`,
      'Failed to remove staff',
    )
  }

  const makeLead = (candidate: EventStaffCandidate) => {
    if (!candidate.schedulingId) return
    const id = candidate.schedulingId
    void run(
      candidate.userId,
      () =>
        updateMutation.mutateAsync(
          create(UpdateUserSchedulingRequestSchema, {
            id,
            role: UserSchedulingRoleEnum.USER_SCHEDULING_ROLE_LEAD,
            updateMask: create(FieldMaskSchema, { paths: ['role'] }),
          }),
        ),
      `${candidateName(candidate)} is now the lead`,
      'Failed to change the lead',
    )
  }

  const updateShift = (candidate: EventStaffCandidate, next: Shift) => {
    if (!candidate.schedulingId) return Promise.resolve(false)
    const id = candidate.schedulingId
    return run(
      candidate.userId,
      () =>
        updateMutation.mutateAsync(
          create(UpdateUserSchedulingRequestSchema, {
            id,
            scheduledAt: next.scheduledAt,
            duration: next.duration,
            updateMask: create(FieldMaskSchema, { paths: ['scheduled_at', 'duration'] }),
          }),
        ),
      `${candidateName(candidate)}'s shift on ${formatTimestamp(next.scheduledAt, 'MMM d', timeZone)} updated`,
      'Failed to update the shift',
    )
  }

  const anyPending = !!busyUserId

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Assign staff</SheetTitle>
          <SheetDescription>
            {availability ? assignStaffSubtitle(availability, eventTimezone) : 'Checking who is free…'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {isLoading ? (
            <div className="flex flex-col gap-2" aria-label="Loading staff">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive">Couldn&apos;t load staff availability. {getErrorDescription(error)}</p>
          ) : !availability || availability.candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No team members yet. Invite people from the Team page.</p>
          ) : (
            <ul aria-label="Team members" className="flex flex-col gap-2">
              {availability.candidates.map((candidate) => {
                const name = candidateName(candidate)
                const detail = candidateDetail(candidate)
                const isAssigned = !!candidate.schedulingId
                const isLead = candidate.role === UserSchedulingRoleEnum.USER_SCHEDULING_ROLE_LEAD
                const isRejected = candidate.status === UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_REJECTED
                const isBusy = busyUserId === candidate.userId
                const scheduling = candidate.schedulingId ? shiftBySchedulingId.get(candidate.schedulingId) : undefined
                const currentShift =
                  !isRejected && scheduling?.scheduledAt
                    ? { scheduledAt: scheduling.scheduledAt, duration: scheduling.duration }
                    : undefined
                return (
                  <li
                    key={candidate.userId}
                    aria-label={name}
                    className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex size-8 shrink-0 select-none items-center justify-center rounded-full text-xs font-semibold text-white',
                        getAvatarColor(name),
                      )}
                    >
                      {getInitials(candidate.firstName, candidate.lastName)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{name}</span>
                        {isAssigned && <RoleTag role={candidate.role} />}
                      </div>
                      <p
                        className={cn(
                          'mt-0.5 truncate text-xs',
                          detail.tone === 'warning'
                            ? 'text-status-warning-fg'
                            : detail.tone === 'success'
                              ? 'text-status-success-fg'
                              : 'text-muted-foreground',
                        )}
                        title={detail.text}
                      >
                        {detail.text}
                      </p>
                      {currentShift && (
                        <StaffShiftEditor
                          name={name}
                          shift={currentShift}
                          timeZone={timeZone}
                          disabled={anyPending}
                          onSave={(next) => updateShift(candidate, next)}
                        />
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {isAssigned && !isLead && !isRejected && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={anyPending}
                          onClick={() => makeLead(candidate)}
                          aria-label={`Make ${name} the lead`}
                        >
                          Make lead
                        </Button>
                      )}
                      {isAssigned ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={anyPending}
                          onClick={() => remove(candidate)}
                          aria-label={`Remove ${name}`}
                        >
                          {isBusy ? 'Saving…' : 'Remove'}
                        </Button>
                      ) : candidate.available ? (
                        <Button
                          type="button"
                          size="sm"
                          disabled={anyPending || !shift}
                          onClick={() => assign(candidate)}
                          aria-label={`Assign ${name}`}
                        >
                          {isBusy ? 'Saving…' : 'Assign'}
                        </Button>
                      ) : (
                        <Button type="button" size="sm" variant="outline" disabled aria-label={`${name} is unavailable`}>
                          Unavailable
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </SheetBody>
        <SheetFooter className="sm:justify-between">
          <span
            className={cn('text-sm', ready ? 'text-status-success-fg' : 'text-muted-foreground')}
            aria-live="polite"
          >
            {availability ? `${assigned} of ${needed} needed` : ''}
          </span>
          <div className="flex items-center gap-3">
            {jobId && (
              <Button asChild variant="link" size="sm">
                <Link to="/pipeline/$id/events/$eventId" params={{ id: jobId, eventId }}>
                  Full logistics →
                </Link>
              </Button>
            )}
            <Button type="button" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
